import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { fmtINR, fmtDate } from "@/lib/format";
import { Download } from "lucide-react";
import { exportXlsx } from "@/lib/xlsx-export";
import WorkflowFilters from "@/components/workflow/WorkflowFilters";
import { applyFilters, bucketCounts } from "@/lib/workflow-filters";

/**
 * Consolidated Quote · SO · PO · PR · GRN register with per-tab
 * Status + Date + Party filters and Excel export.
 *
 * Iter-92 · Phase 1. Data comes from the same endpoints the workflow
 * pages already use — this is a read-only reporting surface.
 */
export default function WorkflowRegisters() {
  const [tab, setTab] = useState("po");
  const [customers, setCustomers] = useState([]);
  const [vendors,   setVendors]   = useState([]);
  const [rows, setRows] = useState({ pr: [], po: [], grn: [], quote: [], so: [] });

  const emptyFilter = { status: "all", from: "", to: "", party: "" };
  const [prFilter, setPrFilter] = useState(emptyFilter);
  const [poFilter, setPoFilter] = useState(emptyFilter);
  const [grnFilter, setGrnFilter] = useState(emptyFilter);
  const [quoteFilter, setQuoteFilter] = useState(emptyFilter);
  const [soFilter, setSoFilter] = useState(emptyFilter);

  useEffect(() => {
    (async () => {
      const [c, v, pr, po, grn, q, so] = await Promise.all([
        api.get("/parties", { params: { party_type: "customer" } }).then((r) => r.data).catch(() => []),
        api.get("/parties", { params: { party_type: "vendor" } }).then((r) => r.data).catch(() => []),
        api.get("/purchase/requisitions").then((r) => r.data).catch(() => []),
        api.get("/purchase/orders").then((r) => r.data).catch(() => []),
        api.get("/purchase/grns").then((r) => r.data).catch(() => []),
        api.get("/sales/quotes").then((r) => r.data).catch(() => []),
        api.get("/sales/orders").then((r) => r.data).catch(() => []),
      ]);
      setCustomers(c); setVendors(v);
      setRows({ pr, po, grn, quote: q, so });
    })();
  }, []);

  const fPr    = useMemo(() => applyFilters(rows.pr,    "pr",    prFilter),    [rows.pr, prFilter]);
  const fPo    = useMemo(() => applyFilters(rows.po,    "po",    poFilter),    [rows.po, poFilter]);
  const fGrn   = useMemo(() => applyFilters(rows.grn,   "grn",   grnFilter),   [rows.grn, grnFilter]);
  const fQuote = useMemo(() => applyFilters(rows.quote, "quote", quoteFilter), [rows.quote, quoteFilter]);
  const fSo    = useMemo(() => applyFilters(rows.so,    "so",    soFilter),    [rows.so, soFilter]);

  const dl = (label, headers, data) => exportXlsx(`${label}-register`, [{ sheet: label, headers, data }]);

  const RowsTable = ({ headers, rows, empty }) => (
    <div className="card-sharp overflow-hidden">
      <table className="w-full dense">
        <thead><tr>{headers.map((h) => <th key={h.k} className={h.right ? "text-right" : ""}>{h.label}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={headers.length} className="text-center py-8 text-muted-foreground">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={r.id || i} data-testid={`register-row-${r.id || i}`}>
              {headers.map((h) => (
                <td key={h.k} className={h.right ? "text-right tabular-nums" : (h.mono ? "font-mono text-xs" : "text-sm")}>
                  {h.fmt ? h.fmt(r) : (r[h.k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const asDate = (k) => (r) => fmtDate(r[k]);
  const asMoney = (k) => (r) => fmtINR(r[k] || 0);

  return (
    <div className="space-y-4" data-testid="workflow-registers-page">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          // Workflow registers — Quote · SO · PO · PR · GRN
        </div>
        <div className="text-[10px] font-mono text-muted-foreground">
          Live data · client-side filters
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="quote" className="rounded-sm" data-testid="reg-tab-quote">Quotes ({rows.quote.length})</TabsTrigger>
          <TabsTrigger value="so" className="rounded-sm" data-testid="reg-tab-so">Sales Orders ({rows.so.length})</TabsTrigger>
          <TabsTrigger value="pr" className="rounded-sm" data-testid="reg-tab-pr">Requisitions ({rows.pr.length})</TabsTrigger>
          <TabsTrigger value="po" className="rounded-sm" data-testid="reg-tab-po">Purchase Orders ({rows.po.length})</TabsTrigger>
          <TabsTrigger value="grn" className="rounded-sm" data-testid="reg-tab-grn">GRN ({rows.grn.length})</TabsTrigger>
        </TabsList>

        {/* Quotes */}
        <TabsContent value="quote" className="mt-4 space-y-3">
          <div className="flex items-end justify-between gap-2">
            <WorkflowFilters value={quoteFilter} onChange={setQuoteFilter} counts={bucketCounts(rows.quote, "quote")} parties={customers} testIdPrefix="reg-quote-filter" />
            <Button variant="outline" size="sm" className="rounded-sm gap-1"
                      onClick={() => dl("Quotes",
                        ["Date","Quote #","Customer","Amount","Valid until","Status"],
                        fQuote.map((q) => [q.date, q.quote_number, q.customer_name || "", q.grand_total || 0, q.valid_until || "", q.status]))}
                      data-testid="reg-quote-export">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
          </div>
          <RowsTable
            rows={fQuote}
            empty="No quotes match the filter"
            headers={[
              { k: "date", label: "Date", fmt: asDate("date") },
              { k: "quote_number", label: "Quote #", mono: true },
              { k: "customer_name", label: "Customer" },
              { k: "grand_total", label: "Amount", right: true, fmt: asMoney("grand_total") },
              { k: "valid_until", label: "Valid until", fmt: (r) => r.valid_until ? fmtDate(r.valid_until) : "—" },
              { k: "status", label: "Status", mono: true },
            ]}
          />
        </TabsContent>

        {/* Sales Orders */}
        <TabsContent value="so" className="mt-4 space-y-3">
          <div className="flex items-end justify-between gap-2">
            <WorkflowFilters value={soFilter} onChange={setSoFilter} counts={bucketCounts(rows.so, "so")} parties={customers} testIdPrefix="reg-so-filter" />
            <Button variant="outline" size="sm" className="rounded-sm gap-1"
                      onClick={() => dl("Sales-Orders",
                        ["Date","SO #","Customer","Amount","Status"],
                        fSo.map((s) => [s.date, s.so_number, s.customer_name || "", s.grand_total || 0, s.status]))}
                      data-testid="reg-so-export">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
          </div>
          <RowsTable
            rows={fSo}
            empty="No sales orders match the filter"
            headers={[
              { k: "date", label: "Date", fmt: asDate("date") },
              { k: "so_number", label: "SO #", mono: true },
              { k: "customer_name", label: "Customer" },
              { k: "grand_total", label: "Amount", right: true, fmt: asMoney("grand_total") },
              { k: "status", label: "Status", mono: true },
            ]}
          />
        </TabsContent>

        {/* PRs */}
        <TabsContent value="pr" className="mt-4 space-y-3">
          <div className="flex items-end justify-between gap-2">
            <WorkflowFilters value={prFilter} onChange={setPrFilter} counts={bucketCounts(rows.pr, "pr")} testIdPrefix="reg-pr-filter" />
            <Button variant="outline" size="sm" className="rounded-sm gap-1"
                      onClick={() => dl("Purchase-Requisitions",
                        ["Date","PR #","Requester","Amount","Status"],
                        fPr.map((p) => [p.date, p.pr_number, p.requester_name || "", p.grand_total || 0, p.status]))}
                      data-testid="reg-pr-export">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
          </div>
          <RowsTable
            rows={fPr}
            empty="No requisitions match the filter"
            headers={[
              { k: "date", label: "Date", fmt: asDate("date") },
              { k: "pr_number", label: "PR #", mono: true },
              { k: "requester_name", label: "Requester" },
              { k: "grand_total", label: "Amount", right: true, fmt: asMoney("grand_total") },
              { k: "status", label: "Status", mono: true },
            ]}
          />
        </TabsContent>

        {/* POs */}
        <TabsContent value="po" className="mt-4 space-y-3">
          <div className="flex items-end justify-between gap-2">
            <WorkflowFilters value={poFilter} onChange={setPoFilter} counts={bucketCounts(rows.po, "po")} parties={vendors} testIdPrefix="reg-po-filter" />
            <Button variant="outline" size="sm" className="rounded-sm gap-1"
                      onClick={() => dl("Purchase-Orders",
                        ["Date","PO #","Vendor","Amount","Approval","Status"],
                        fPo.map((p) => [p.date, p.po_number, p.vendor_name || "", p.grand_total || 0, p.approval_status, p.status]))}
                      data-testid="reg-po-export">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
          </div>
          <RowsTable
            rows={fPo}
            empty="No POs match the filter"
            headers={[
              { k: "date", label: "Date", fmt: asDate("date") },
              { k: "po_number", label: "PO #", mono: true },
              { k: "vendor_name", label: "Vendor" },
              { k: "grand_total", label: "Amount", right: true, fmt: asMoney("grand_total") },
              { k: "approval_status", label: "Approval", mono: true },
              { k: "status", label: "Status", mono: true },
            ]}
          />
        </TabsContent>

        {/* GRNs */}
        <TabsContent value="grn" className="mt-4 space-y-3">
          <div className="flex items-end justify-between gap-2">
            <WorkflowFilters value={grnFilter} onChange={setGrnFilter} counts={bucketCounts(rows.grn, "grn")} parties={vendors} testIdPrefix="reg-grn-filter" />
            <Button variant="outline" size="sm" className="rounded-sm gap-1"
                      onClick={() => dl("Goods-Receipts",
                        ["Date","GRN #","Vendor","PO ref","Vehicle","Status"],
                        fGrn.map((g) => [g.date, g.grn_number, g.vendor_name || "", g.source_po_id || "", g.vehicle_no || "", g.status]))}
                      data-testid="reg-grn-export">
              <Download className="w-3.5 h-3.5" /> Excel
            </Button>
          </div>
          <RowsTable
            rows={fGrn}
            empty="No goods receipts match the filter"
            headers={[
              { k: "date", label: "Date", fmt: asDate("date") },
              { k: "grn_number", label: "GRN #", mono: true },
              { k: "vendor_name", label: "Vendor" },
              { k: "source_po_id", label: "PO ref", mono: true, fmt: (r) => r.source_po_id?.slice(0, 8) || "—" },
              { k: "vehicle_no", label: "Vehicle", mono: true },
              { k: "status", label: "Status", mono: true },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
